import { useEffect, useMemo, useState, type CSSProperties } from 'react'

import { useTranslate } from '@/i18n/useTranslate'
import type {
    DepositBonusEligibilityResponse,
    DepositBonusEligibilityStep,
    DepositBonusPromotion
} from '@/types/pos/depositBonus'
import {
    formatPromotionTotalAmount,
    getBonusCheckThroughIndex,
    getCampaignValidityDays,
    getPromotionTotalAmount,
    isClaimedBonusStep,
    isNextBonusStep,
    sortBonusSteps
} from '@/utilities/depositBonusHelper'

const PREVIEW_HOLD_MS = 400
const STEP_CHECK_MS = 800

type VendorBonusOverlayProps = {
    promotion: DepositBonusPromotion
    eligibility: DepositBonusEligibilityResponse
    vendorName: string
    vendorLogoUrl?: string | null
    onContinue: () => void
}

const CalendarIcon = () => (
    <svg viewBox="0 0 32 32" aria-hidden="true">
        <rect
            x="4"
            y="8"
            width="24"
            height="20"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        />
        <path
            d="M4 14h24"
            stroke="currentColor"
            strokeWidth="2"
        />
        <path
            d="M10 4v6M22 4v6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
)

const StopwatchIcon = () => (
    <svg viewBox="0 0 32 32" aria-hidden="true">
        <circle
            cx="16"
            cy="18"
            r="10"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        />
        <path
            d="M16 18V12M13 4h6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
        <path
            d="M24 8l2.5 2.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
        />
    </svg>
)

const PaymentIcon = () => (
    <svg viewBox="0 0 32 32" aria-hidden="true">
        <rect
            x="3"
            y="8"
            width="26"
            height="16"
            rx="3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
        />
        <path d="M3 14h26" stroke="currentColor" strokeWidth="2" />
        <rect x="7" y="18" width="8" height="2.4" rx="1" fill="currentColor" />
    </svg>
)

const ordinalKey = (ordinal: number) => {
    if (ordinal === 1) return 'first'
    if (ordinal === 2) return 'second'
    if (ordinal === 3) return 'third'
    return 'nth'
}

const StepFx = ({
    kind,
    count
}: {
    kind: 'checking' | 'available'
    count: number
}) => (
    <span
        className={`vendor-bonus-overlay__fx vendor-bonus-overlay__fx--${kind}`}
        aria-hidden="true">
        {Array.from({ length: count }, (_, index) => (
            <span
                key={index}
                className="vendor-bonus-overlay__particle"
                style={{ '--i': index } as CSSProperties}
            />
        ))}
    </span>
)

export default function VendorBonusOverlay({
    promotion,
    eligibility,
    vendorName,
    vendorLogoUrl,
    onContinue
}: VendorBonusOverlayProps) {
    const { t } = useTranslate()
    const [checkingIndex, setCheckingIndex] = useState(-1)
    const [settledCount, setSettledCount] = useState(0)
    const [sequenceDone, setSequenceDone] = useState(false)

    const steps = useMemo(
        () => sortBonusSteps(eligibility.steps ?? []),
        [eligibility.steps]
    )
    const checkThroughIndex = useMemo(
        () => getBonusCheckThroughIndex(steps),
        [steps]
    )
    const allSettled = sequenceDone
    const totalAmount = getPromotionTotalAmount(promotion)
    const validityDays = getCampaignValidityDays(promotion) ?? 30
    const displayName = vendorName || promotion.vendorName
    const currency = promotion.currencyCode || 'RSD'
    const nextStep = steps.find((step) => isNextBonusStep(step.status))
    const lastClaimedIndex = useMemo(() => {
        let lastIndex = -1
        steps.forEach((step, index) => {
            if (isClaimedBonusStep(step.status)) lastIndex = index
        })
        return lastIndex
    }, [steps])
    const nextRemainingStep = steps.find(
        (step) => !isClaimedBonusStep(step.status) && !isNextBonusStep(step.status)
    )
    const rewardAmount = eligibility.reward?.amount ?? nextStep?.amount ?? 0
    const usedToday = eligibility.alreadyUsedToday && !eligibility.isEligible

    useEffect(() => {
        let cancelled = false
        const timeouts: number[] = []
        const wait = (ms: number) =>
            new Promise<void>((resolve) => {
                timeouts.push(window.setTimeout(resolve, ms))
            })

        const runSequence = async () => {
            await wait(PREVIEW_HOLD_MS)
            if (cancelled) return

            for (let index = 0; index <= checkThroughIndex; index += 1) {
                if (cancelled) return
                setCheckingIndex(index)
                await wait(STEP_CHECK_MS)
                if (cancelled) return
                setSettledCount(index + 1)
            }
            if (cancelled) return
            setCheckingIndex(-1)
            setSettledCount(steps.length)
            setSequenceDone(true)
        }

        void runSequence()

        return () => {
            cancelled = true
            timeouts.forEach((id) => window.clearTimeout(id))
        }
    }, [checkThroughIndex, steps.length])

    const statusCopy = (() => {
        if (!allSettled) {
            return {
                kind: 'preview' as const,
                text: t('vendorBonusOverlay.summary', {
                    count: promotion.maxBonusCount,
                    amount: formatPromotionTotalAmount(totalAmount),
                    currency
                })
            }
        }
        if (eligibility.isEligible) {
            return {
                kind: 'eligible' as const,
                text: t('vendorBonusOverlay.eligible')
            }
        }
        if (eligibility.alreadyUsedToday) {
            return {
                kind: 'usedToday' as const,
                text: t('vendorBonusOverlay.usedToday')
            }
        }
        return {
            kind: 'preview' as const,
            text: t('vendorBonusOverlay.summary', {
                count: promotion.maxBonusCount,
                amount: formatPromotionTotalAmount(totalAmount),
                currency
            })
        }
    })()

    const stepClassName = (step: DepositBonusEligibilityStep, index: number) => {
        const classes = ['vendor-bonus-overlay__step']
        if (index === checkingIndex) {
            classes.push('vendor-bonus-overlay__step--checking')
            return classes.join(' ')
        }
        if (index >= settledCount) {
            classes.push('vendor-bonus-overlay__step--pending')
            return classes.join(' ')
        }
        if (isNextBonusStep(step.status)) {
            classes.push('vendor-bonus-overlay__step--available')
        } else if (isClaimedBonusStep(step.status)) {
            classes.push('vendor-bonus-overlay__step--claimed')
            if (usedToday && index === lastClaimedIndex) {
                classes.push('vendor-bonus-overlay__step--used-today')
            }
        } else if (step.status === 'LOCKED') {
            classes.push('vendor-bonus-overlay__step--locked')
        } else {
            classes.push('vendor-bonus-overlay__step--upcoming')
        }
        return classes.join(' ')
    }

    const isStepSettled = (index: number) => index < settledCount

    return (
        <div className="vendor-bonus-overlay" role="dialog" aria-modal="true">
            <div className="vendor-bonus-overlay__panel">
                <div className="vendor-bonus-overlay__hero">
                    <div className="vendor-bonus-overlay__header">
                        <div className="vendor-bonus-overlay__brand">
                            <div className="vendor-bonus-overlay__logo">
                                {vendorLogoUrl ? (
                                    <img src={vendorLogoUrl} alt="" />
                                ) : (
                                    <span>{displayName.slice(0, 1)}</span>
                                )}
                            </div>
                            <div className="vendor-bonus-overlay__brand-copy">
                                <p className="vendor-bonus-overlay__kicker">
                                    {t('vendorBonusOverlay.exclusiveOffer')}
                                </p>
                                <h2>{displayName}</h2>
                                <p className="vendor-bonus-overlay__campaign">
                                    {promotion.name}
                                </p>
                            </div>
                        </div>
                        <span className="vendor-bonus-overlay__new">
                            {t('vendorBonusOverlay.newBadge')}
                        </span>
                    </div>

                    <p
                        className={`vendor-bonus-overlay__status vendor-bonus-overlay__status--${statusCopy.kind}`}>
                        {statusCopy.kind === 'eligible' ||
                        statusCopy.kind === 'usedToday' ? (
                            <span className="vendor-bonus-overlay__status-check">
                                ✓
                            </span>
                        ) : null}
                        {statusCopy.text}
                    </p>

                    <div
                        className="vendor-bonus-overlay__steps"
                        style={{
                            gridTemplateColumns: `repeat(${Math.max(steps.length, 1)}, minmax(0, 1fr))`
                        }}>
                        {steps.map((step, index) => {
                            const isChecking = index === checkingIndex
                            const isAward =
                                isStepSettled(index) &&
                                isNextBonusStep(step.status)

                            return (
                                <div
                                    key={step.depositOrdinal}
                                    className={stepClassName(step, index)}>
                                    {isChecking ? (
                                        <>
                                            <StepFx kind="checking" count={8} />
                                            <span className="vendor-bonus-overlay__spinner" />
                                        </>
                                    ) : null}
                                    {isAward ? (
                                        <>
                                            <span className="vendor-bonus-overlay__shimmer" />
                                            <StepFx kind="available" count={10} />
                                            <span className="vendor-bonus-overlay__your-bonus">
                                                ✓{' '}
                                                {t(
                                                    'vendorBonusOverlay.yourBonus'
                                                )}
                                            </span>
                                        </>
                                    ) : null}
                                    {isStepSettled(index) &&
                                    isClaimedBonusStep(step.status) &&
                                    usedToday &&
                                    index === lastClaimedIndex ? (
                                        <span className="vendor-bonus-overlay__used-today">
                                            ✓{' '}
                                            {t('vendorBonusOverlay.usedTodayBadge')}
                                        </span>
                                    ) : isStepSettled(index) &&
                                      isClaimedBonusStep(step.status) ? (
                                        <span className="vendor-bonus-overlay__claimed">
                                            ✓
                                        </span>
                                    ) : null}
                                    <p className="vendor-bonus-overlay__step-label">
                                        {t('vendorBonusOverlay.depositLabel', {
                                            ordinal: step.depositOrdinal
                                        })}
                                    </p>
                                    <p className="vendor-bonus-overlay__step-amount">
                                        {formatPromotionTotalAmount(step.amount)}
                                    </p>
                                    <p className="vendor-bonus-overlay__step-currency">
                                        {step.currencyCode || currency}
                                    </p>
                                </div>
                            )
                        })}
                    </div>

                    {allSettled &&
                    eligibility.isEligible &&
                    rewardAmount > 0 ? (
                        <div className="vendor-bonus-overlay__banner">
                            <span className="vendor-bonus-overlay__banner-check">
                                ✓
                            </span>
                            {t('vendorBonusOverlay.bannerEligible', {
                                amount: formatPromotionTotalAmount(rewardAmount),
                                currency:
                                    eligibility.reward?.currencyCode || currency,
                                ordinalLabel: t(
                                    `vendorBonusOverlay.ordinal.${ordinalKey(nextStep?.depositOrdinal ?? eligibility.depositOrdinal ?? 1)}`,
                                    { n: nextStep?.depositOrdinal ?? 1 }
                                )
                            })}
                        </div>
                    ) : null}
                    {allSettled && usedToday ? (
                        <div className="vendor-bonus-overlay__banner vendor-bonus-overlay__banner--used-today">
                            <span className="vendor-bonus-overlay__banner-check">
                                ✓
                            </span>
                            {nextRemainingStep
                                ? t('vendorBonusOverlay.bannerUsedTodayNext', {
                                      ordinal: nextRemainingStep.depositOrdinal
                                  })
                                : t('vendorBonusOverlay.bannerUsedToday')}
                        </div>
                    ) : null}
                </div>

                <div className="vendor-bonus-overlay__footer">
                    <div className="vendor-bonus-overlay__facts">
                        <div className="vendor-bonus-overlay__fact">
                            <CalendarIcon />
                            <span>
                                {t('vendorBonusOverlay.factDays', {
                                    count: promotion.maxBonusCount
                                })}
                            </span>
                        </div>
                        <div className="vendor-bonus-overlay__fact">
                            <StopwatchIcon />
                            <span>
                                {t('vendorBonusOverlay.factValid', {
                                    days: validityDays
                                })}
                            </span>
                        </div>
                        <div className="vendor-bonus-overlay__fact">
                            <PaymentIcon />
                            <span>{t('vendorBonusOverlay.factPayment')}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="vendor-bonus-overlay__continue"
                        onClick={onContinue}>
                        {t('vendorBonusOverlay.continue')}
                    </button>
                </div>
            </div>
        </div>
    )
}
